/**
 * Progress Merger Utility
 * Handles conflict resolution when syncing game progress between devices
 * 
 * Strategy:
 * - Timestamp-based: Prefer most recent data when timestamps available
 * - Best-progress wins: Keep highest stars/scores per level
 * - Union of unlocked levels: Combine progress from all sources
 * - Preserve user preferences: Keep most specific calibration data
 */

/**
 * Merge two progress objects intelligently
 * @param {Object} localProgress - Progress from localStorage
 * @param {Object} s3Progress - Progress from S3
 * @returns {Object} Merged progress object
 */
export const mergeProgress = (localProgress, s3Progress) => {
  // If only one source has data, return it
  if (!localProgress && !s3Progress) {
    return getDefaultProgress();
  }
  if (!localProgress) return { ...s3Progress };
  if (!s3Progress) return { ...localProgress };

  console.log('🔄 Merging progress from localStorage and S3...');

  // Start with a base structure
  const merged = {
    unlockedLevels: [],
    completedLevels: [],
    levelStars: {},
    highScores: {},
    user_sa_frequency: null,
    last_updated: null,
  };

  // 1. UNLOCKED LEVELS - Take union (user should not lose unlocked levels)
  const unlockedSet = new Set([
    ...(localProgress.unlockedLevels || []),
    ...(s3Progress.unlockedLevels || []),
  ]);
  merged.unlockedLevels = Array.from(unlockedSet).sort((a, b) => a - b);

  // 2. COMPLETED LEVELS - Take union
  const completedSet = new Set([
    ...(localProgress.completedLevels || []),
    ...(s3Progress.completedLevels || []),
  ]);
  merged.completedLevels = Array.from(completedSet).sort((a, b) => a - b);

  // 3. LEVEL STARS - Keep best (highest) for each level
  const allLevelIds = new Set([
    ...Object.keys(localProgress.levelStars || {}),
    ...Object.keys(s3Progress.levelStars || {}),
  ]);

  allLevelIds.forEach((levelId) => {
    const localStars = (localProgress.levelStars || {})[levelId] || 0;
    const s3Stars = (s3Progress.levelStars || {})[levelId] || 0;
    merged.levelStars[levelId] = Math.max(localStars, s3Stars);
  });

  // 4. HIGH SCORES - Keep best (highest) for each level
  const allScoreLevels = new Set([
    ...Object.keys(localProgress.highScores || {}),
    ...Object.keys(s3Progress.highScores || {}),
  ]);

  allScoreLevels.forEach((levelId) => {
    const localScore = (localProgress.highScores || {})[levelId] || 0;
    const s3Score = (s3Progress.highScores || {})[levelId] || 0;
    merged.highScores[levelId] = Math.max(localScore, s3Score);
  });

  // 5. USER SA FREQUENCY (calibration) - Prefer most recent with fallback
  // Use timestamp if available, otherwise prefer S3 (cloud is "source of truth")
  const localTime = localProgress.last_updated
    ? new Date(localProgress.last_updated).getTime()
    : 0;
  const s3Time = s3Progress.last_updated
    ? new Date(s3Progress.last_updated).getTime()
    : 0;

  if (localTime > s3Time) {
    merged.user_sa_frequency = localProgress.user_sa_frequency || s3Progress.user_sa_frequency;
    merged.last_updated = localProgress.last_updated;
  } else {
    merged.user_sa_frequency = s3Progress.user_sa_frequency || localProgress.user_sa_frequency;
    merged.last_updated = s3Progress.last_updated;
  }

  // 6. UNLOCKED ACHIEVEMENTS - Take union (preserve all earned achievements)
  // Each entry is { id: string, unlockedAt: ISO string }
  // Merge by id: keep earliest unlockedAt if both sources have the same id
  const achievementMap = new Map();
  [...(localProgress.unlockedAchievements || []), ...(s3Progress.unlockedAchievements || [])].forEach((entry) => {
    if (!entry?.id) return;
    const existing = achievementMap.get(entry.id);
    if (!existing) {
      achievementMap.set(entry.id, entry);
    } else {
      // Keep the earliest unlock timestamp
      const existingTime = existing.unlockedAt ? new Date(existing.unlockedAt).getTime() : Infinity;
      const entryTime = entry.unlockedAt ? new Date(entry.unlockedAt).getTime() : Infinity;
      if (entryTime < existingTime) achievementMap.set(entry.id, entry);
    }
  });
  merged.unlockedAchievements = Array.from(achievementMap.values());

  // Log merge summary
  const stats = getMergeStats(localProgress, s3Progress, merged);
  console.log('✅ Merge complete:', stats);

  return merged;
};

/**
 * Get default/empty progress structure
 * @returns {Object} Default progress object
 */
export const getDefaultProgress = () => ({
  unlockedLevels: [1], // Level 1 always unlocked
  completedLevels: [],
  levelStars: {},
  highScores: {},
  user_sa_frequency: null,
  last_updated: null,
});

/**
 * Calculate merge statistics for logging/UI
 * @param {Object} local - Local progress
 * @param {Object} s3 - S3 progress
 * @param {Object} merged - Merged result
 * @returns {Object} Statistics about the merge
 */
export const getMergeStats = (local, s3, merged) => {
  const localLevels = (local?.completedLevels || []).length;
  const s3Levels = (s3?.completedLevels || []).length;
  const mergedLevels = (merged?.completedLevels || []).length;

  const localStars = Object.values(local?.levelStars || {}).reduce(
    (sum, stars) => sum + stars,
    0
  );
  const s3Stars = Object.values(s3?.levelStars || {}).reduce(
    (sum, stars) => sum + stars,
    0
  );
  const mergedStars = Object.values(merged?.levelStars || {}).reduce(
    (sum, stars) => sum + stars,
    0
  );

  return {
    levels: {
      local: localLevels,
      s3: s3Levels,
      merged: mergedLevels,
      gained: mergedLevels - Math.max(localLevels, s3Levels),
    },
    stars: {
      local: localStars,
      s3: s3Stars,
      merged: mergedStars,
      gained: mergedStars - Math.max(localStars, s3Stars),
    },
    unlockedLevels: {
      local: (local?.unlockedLevels || []).length,
      s3: (s3?.unlockedLevels || []).length,
      merged: (merged?.unlockedLevels || []).length,
    },
  };
};

/**
 * Check if two progress objects are significantly different
 * @param {Object} progress1 - First progress object
 * @param {Object} progress2 - Second progress object
 * @returns {boolean} True if significantly different
 */
export const hasSignificantDifference = (progress1, progress2) => {
  if (!progress1 || !progress2) return true;

  // Check completed levels
  const levels1 = progress1.completedLevels || [];
  const levels2 = progress2.completedLevels || [];
  if (levels1.length !== levels2.length) return true;

  // Check total stars
  const stars1 = Object.values(progress1.levelStars || {}).reduce(
    (sum, s) => sum + s,
    0
  );
  const stars2 = Object.values(progress2.levelStars || {}).reduce(
    (sum, s) => sum + s,
    0
  );
  if (stars1 !== stars2) return true;

  // Check calibration
  if (progress1.user_sa_frequency !== progress2.user_sa_frequency) return true;

  return false;
};

/**
 * Convert GameContext format to S3 API format
 * @param {Object} contextProgress - Progress from GameContext
 * @param {string} userId - Google user ID
 * @returns {Object} S3-compatible progress object
 */
export const convertToS3Format = (contextProgress, userId) => {
  return {
    user_id: userId,
    user_sa_frequency: contextProgress.user_sa_frequency || null,
    current_level: Math.max(...(contextProgress.unlockedLevels || [1])),
    total_stars: Object.values(contextProgress.levelStars || {}).reduce(
      (sum, stars) => sum + stars,
      0
    ),
    unlocked_levels: contextProgress.unlockedLevels || [1],
    completed_levels: (contextProgress.completedLevels || []).map((levelId) => ({
      level_id: levelId,
      stars: contextProgress.levelStars[levelId] || 0,
      best_score: contextProgress.highScores[levelId] || 0,
    })),
    last_updated: new Date().toISOString(),
  };
};

/**
 * Convert S3 API format to GameContext format
 * @param {Object} s3Progress - Progress from S3
 * @returns {Object} GameContext-compatible progress object
 */
export const convertFromS3Format = (s3Progress) => {
  const contextProgress = {
    unlockedLevels: s3Progress.unlocked_levels || [1],
    completedLevels: [],
    levelStars: {},
    highScores: {},
    user_sa_frequency: s3Progress.user_sa_frequency || null,
    last_updated: s3Progress.last_updated || null,
  };

  // Extract from completed_levels array
  (s3Progress.completed_levels || []).forEach((level) => {
    if (level.level_id) {
      contextProgress.completedLevels.push(level.level_id);
      contextProgress.levelStars[level.level_id] = level.stars || 0;
      contextProgress.highScores[level.level_id] = level.best_score || 0;
    }
  });

  return contextProgress;
};

export default {
  mergeProgress,
  getDefaultProgress,
  getMergeStats,
  hasSignificantDifference,
  convertToS3Format,
  convertFromS3Format,
};
